import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Save, Upload, CalendarClock, ShieldCheck, ShieldAlert, Bell, Loader2, ExternalLink } from "lucide-react";

const PROVIDERS = ["FlightSafety International", "CAE", "Simcom", "Simuflite", "Gulfstream Training Academy", "Other"];

// Currency → visual tone (matches _compute_currency on backend)
export const CURRENCY_TONE = {
  green:  { bg: "rgba(46,213,115,0.16)",  border: "#2ED573", text: "#4EDF8B", label: "PIC CURRENT",     dot: "#2ED573" },
  orange: { bg: "rgba(255,159,64,0.14)",  border: "#FF9F40", text: "#FFB566", label: "SIC CURRENT",     dot: "#FF9F40" },
  yellow: { bg: "rgba(245,200,66,0.14)",  border: "#F5C842", text: "#F5C842", label: "EXPIRING",        dot: "#F5C842" },
  red:    { bg: "rgba(235,87,87,0.14)",   border: "#EB5757", text: "#FF7A7A", label: "NO DATA / EXPIRED", dot: "#EB5757" },
};

export function computeCurrency(rec) {
  if (!rec || !rec.last_training_date) return { status: "red", months_since: null, reason: "no_data", needs_recurrent_soon: false };
  const dt = new Date(rec.last_training_date);
  if (isNaN(dt.getTime())) return { status: "red", months_since: null, reason: "bad_date", needs_recurrent_soon: false };
  const months = Math.floor((Date.now() - dt.getTime()) / (1000 * 60 * 60 * 24 * 30.4375));
  const needs_soon = months >= 10 && months <= 11;
  if (months < 0) return { status: "green", months_since: months, needs_recurrent_soon: false };
  if (months <= 12) return { status: "green", months_since: months, needs_recurrent_soon: needs_soon };
  if (months <= 23) return { status: "orange", months_since: months, needs_recurrent_soon: false };
  if (months === 24) return { status: "yellow", months_since: months, needs_recurrent_soon: false };
  return { status: "red", reason: "expired", months_since: months, needs_recurrent_soon: false };
}

export default function TypeRatingDetail({ code, initial, onClose, onSaved }) {
  const [rec, setRec] = useState({
    last_training_date: initial?.last_training_date || "",
    training_provider: initial?.training_provider || "",
    training_provider_location: initial?.training_provider_location || "",
    cert_file_id: initial?.cert_file_id || "",
    cert_file_url: initial?.cert_file_id ? `/api/files/${initial.cert_file_id}` : "",
    pic_hours: initial?.pic_hours ?? "",
    sic_hours: initial?.sic_hours ?? "",
    is_pic: initial?.is_pic ?? true,
    notes: initial?.notes || "",
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [schedule, setSchedule] = useState({ preferred_provider: "", preferred_location: "", preferred_month: "", notes: "" });
  const fileRef = useRef(null);

  const currency = computeCurrency(rec);
  const tone = CURRENCY_TONE[currency.status] || CURRENCY_TONE.red;

  const uploadCert = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("kind", "training_cert");
      const r = await api.post("/files/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      setRec((s) => ({ ...s, cert_file_id: r.data.id, cert_file_url: r.data.url }));
      toast.success("Training certificate uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        last_training_date: rec.last_training_date || null,
        training_provider: rec.training_provider || null,
        training_provider_location: rec.training_provider_location || null,
        cert_file_id: rec.cert_file_id || null,
        pic_hours: rec.pic_hours === "" ? null : Number(rec.pic_hours),
        sic_hours: rec.sic_hours === "" ? null : Number(rec.sic_hours),
        is_pic: !!rec.is_pic,
        notes: rec.notes || null,
      };
      const r = await api.put(`/pilot/type-rating-data/${encodeURIComponent(code)}`, payload);
      toast.success("Training data saved");
      onSaved && onSaved(code, r.data);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const scheduleRecurrent = async () => {
    setScheduling(true);
    try {
      await api.post(`/pilot/type-rating-data/${encodeURIComponent(code)}/schedule-recurrent`, schedule);
      toast.success("Recurrent request queued — PJP will reach out within 24h");
      setScheduleOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Schedule failed");
    } finally { setScheduling(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center sm:p-4" data-testid={`type-rating-modal-${code}`}>
      <div className="bg-obsidian border border-white/15 rounded-t-2xl sm:rounded-2xl max-w-lg w-full max-h-[92vh] overflow-y-auto"
           style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
        {/* Header */}
        <div className="sticky top-0 z-10 bg-obsidian/95 backdrop-blur border-b border-white/10 p-4 sm:p-5 flex items-center gap-3">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border mono text-sm font-bold"
               style={{ background: tone.bg, borderColor: tone.border, color: tone.text }}>
            <span className="h-2 w-2 rounded-full" style={{ background: tone.dot, boxShadow: `0 0 8px ${tone.dot}` }} />
            {code}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display text-lg font-semibold text-white truncate">Training & Currency</div>
            <div className="mono text-[10px] tracking-[0.24em] uppercase" style={{ color: tone.text }}>
              {tone.label}
              {currency.months_since !== null && ` · ${currency.months_since} MO`}
            </div>
          </div>
          <button onClick={onClose} className="p-2 -mr-2 text-white/60 hover:text-white" data-testid={`type-modal-close-${code}`}>
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-4 sm:p-5 space-y-5">
          {/* Recurrent-due banner */}
          {currency.needs_recurrent_soon && !scheduleOpen && (
            <div className="rounded-xl border border-orange-hud/40 bg-orange-hud/10 p-4 flex items-start gap-3" data-testid="recurrent-banner">
              <Bell className="h-5 w-5 text-orange-hud flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-white text-sm">Recurrent training approaching</div>
                <div className="text-xs text-white/70 mt-0.5">
                  You're {currency.months_since} months in — PJP can help schedule your 12-month recurrent through FlightSafety, CAE, or Simcom.
                </div>
                <button
                  onClick={() => setScheduleOpen(true)}
                  data-testid="btn-open-schedule"
                  className="mt-3 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full font-semibold text-xs min-h-[36px]"
                  style={{ background: "#FF9F40", color: "#0A0F1A" }}
                >
                  <CalendarClock className="h-3.5 w-3.5" /> Schedule via PJP
                </button>
              </div>
            </div>
          )}

          {currency.status === "red" && currency.reason === "expired" && (
            <div className="rounded-xl border border-[#EB5757]/40 bg-[#EB5757]/10 p-4 flex items-start gap-3">
              <ShieldAlert className="h-5 w-5 text-[#EB5757] flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="font-semibold text-white text-sm">Currency expired</div>
                <div className="text-xs text-white/70 mt-0.5">Insurance won't cover trips in this type until recurrent is complete.</div>
                <button
                  onClick={() => setScheduleOpen(true)}
                  data-testid="btn-open-schedule-expired"
                  className="mt-3 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full font-semibold text-xs min-h-[36px]"
                  style={{ background: "#EB5757", color: "#fff" }}
                >
                  <CalendarClock className="h-3.5 w-3.5" /> Get Current via PJP
                </button>
              </div>
            </div>
          )}

          {scheduleOpen && (
            <div className="rounded-xl border border-sky/30 bg-sky/[0.05] p-4 space-y-3" data-testid="schedule-panel">
              <div className="mono text-[10px] tracking-[0.24em] uppercase text-sky">// SCHEDULE RECURRENT</div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <div className="eyebrow-muted mb-1.5">Preferred Provider</div>
                  <select className="input !py-2 !text-sm" value={schedule.preferred_provider} onChange={(e) => setSchedule({ ...schedule, preferred_provider: e.target.value })} data-testid="sched-provider">
                    <option value="">Any</option>
                    {PROVIDERS.map((p) => <option key={p}>{p}</option>)}
                  </select>
                </div>
                <div>
                  <div className="eyebrow-muted mb-1.5">Location (city/ICAO)</div>
                  <input className="input !py-2 !text-sm" value={schedule.preferred_location} onChange={(e) => setSchedule({ ...schedule, preferred_location: e.target.value })} placeholder="DFW, LGB, MMU…" data-testid="sched-location" style={{ fontSize: 16 }} />
                </div>
              </div>
              <div>
                <div className="eyebrow-muted mb-1.5">Preferred Month</div>
                <input type="month" className="input !py-2 !text-sm" value={schedule.preferred_month} onChange={(e) => setSchedule({ ...schedule, preferred_month: e.target.value })} data-testid="sched-month" style={{ fontSize: 16 }} />
              </div>
              <div>
                <div className="eyebrow-muted mb-1.5">Notes</div>
                <textarea rows={2} className="input !py-2 !text-sm" value={schedule.notes} onChange={(e) => setSchedule({ ...schedule, notes: e.target.value })} placeholder="Any scheduling constraints, preferred simulator dates…" data-testid="sched-notes" style={{ fontSize: 16 }} />
              </div>
              <div className="flex gap-2 justify-end">
                <button onClick={() => setScheduleOpen(false)} className="btn btn-sm">Cancel</button>
                <button onClick={scheduleRecurrent} disabled={scheduling} className="btn btn-sm btn-primary" data-testid="btn-submit-schedule">
                  {scheduling ? "Sending…" : "Send Request"}
                </button>
              </div>
            </div>
          )}

          {/* Last training date */}
          <div>
            <div className="eyebrow-muted mb-1.5">Last Training Date</div>
            <input
              type="date"
              className="input"
              value={rec.last_training_date}
              onChange={(e) => setRec((s) => ({ ...s, last_training_date: e.target.value }))}
              max={new Date().toISOString().slice(0, 10)}
              data-testid="input-training-date"
              style={{ fontSize: 16 }}
            />
            <div className="mt-1.5 text-[11px] text-white/45">
              Currency window: <span className="text-[#4EDF8B]">0–12 mo = PIC</span> · <span className="text-[#FFB566]">13–23 mo = SIC</span> · <span className="text-[#F5C842]">24 mo = Expiring</span> · <span className="text-[#FF7A7A]">25+ mo = Expired</span>
            </div>
          </div>

          {/* Provider */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="eyebrow-muted mb-1.5">Training Provider</div>
              <select className="input" value={rec.training_provider} onChange={(e) => setRec((s) => ({ ...s, training_provider: e.target.value }))} data-testid="input-training-provider">
                <option value="">Select provider…</option>
                {PROVIDERS.map((p) => <option key={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <div className="eyebrow-muted mb-1.5">Location</div>
              <input className="input" value={rec.training_provider_location} onChange={(e) => setRec((s) => ({ ...s, training_provider_location: e.target.value }))} placeholder="DFW, LGB, TEB…" data-testid="input-training-location" style={{ fontSize: 16 }} />
            </div>
          </div>

          {/* Cert upload */}
          <div>
            <div className="eyebrow-muted mb-1.5">Training Certificate</div>
            {rec.cert_file_id ? (
              <div className="flex items-center gap-2 p-3 rounded-lg border border-avail/40 bg-avail/10">
                <ShieldCheck className="h-4 w-4 text-avail" />
                <div className="text-sm text-avail flex-1">Certificate on file</div>
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL || ""}/api/files/${rec.cert_file_id}`}
                  target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-sky hover:underline mono"
                  data-testid="btn-view-cert"
                >
                  View <ExternalLink className="h-3 w-3" />
                </a>
                <button onClick={() => setRec((s) => ({ ...s, cert_file_id: "", cert_file_url: "" }))} className="text-white/50 hover:text-white text-xs" data-testid="btn-remove-cert">Remove</button>
              </div>
            ) : (
              <>
                <input ref={fileRef} type="file" accept="image/*,application/pdf" onChange={(e) => uploadCert(e.target.files?.[0])} className="hidden" data-testid="input-cert-file" />
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  data-testid="btn-upload-cert"
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg border border-dashed border-white/20 text-white/70 hover:border-sky/50 hover:text-white text-sm min-h-[48px]"
                >
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                  {uploading ? "Uploading…" : "Upload PDF or Photo"}
                </button>
              </>
            )}
          </div>

          {/* PIC / SIC hours */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="eyebrow-muted mb-1.5">Time in Type — PIC (hrs)</div>
              <input type="number" min="0" className="input" value={rec.pic_hours} onChange={(e) => setRec((s) => ({ ...s, pic_hours: e.target.value }))} placeholder="0" data-testid="input-pic-hours" style={{ fontSize: 16 }} />
            </div>
            <div>
              <div className="eyebrow-muted mb-1.5">Time in Type — SIC (hrs)</div>
              <input type="number" min="0" className="input" value={rec.sic_hours} onChange={(e) => setRec((s) => ({ ...s, sic_hours: e.target.value }))} placeholder="0" data-testid="input-sic-hours" style={{ fontSize: 16 }} />
            </div>
          </div>

          <div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={!!rec.is_pic} onChange={(e) => setRec((s) => ({ ...s, is_pic: e.target.checked }))} className="w-4 h-4" data-testid="input-is-pic" />
              <span className="text-sm text-white/85">Currently flying as PIC in-type</span>
            </label>
          </div>

          <div>
            <div className="eyebrow-muted mb-1.5">Notes</div>
            <textarea rows={2} className="input" value={rec.notes} onChange={(e) => setRec((s) => ({ ...s, notes: e.target.value }))} placeholder="Simulator, IOE completion, mentor pilot…" data-testid="input-notes" style={{ fontSize: 16 }} />
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-obsidian/95 backdrop-blur border-t border-white/10 p-4 flex gap-2 justify-end" style={{ paddingBottom: "calc(1rem + env(safe-area-inset-bottom))" }}>
          <button onClick={onClose} className="btn" data-testid="btn-cancel-type-modal">Cancel</button>
          <button onClick={save} disabled={saving} className="btn btn-primary" data-testid="btn-save-type-data">
            <Save className="h-3.5 w-3.5" />
            {saving ? "Saving…" : "Save Training Data"}
          </button>
        </div>
      </div>
    </div>
  );
}
